/* eslint-disable react/jsx-props-no-spreading */ // that's on the Render
import React, { useEffect, useState } from 'react';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import PropTypes from 'prop-types';
import DoubleChoice from './questions/DoubleChoice';
import SingleChoice from './questions/SingleChoice';
import Selector from './questions/Selector';
import Header from './common/Header';
import Conclusion from './Conclusion';

const components = {
  DoubleChoice,
  SingleChoice,
  Selector,
  Conclusion,
};

/** Handle navigation between the nodes of the data-tree */
function Wizard({ tree }) {
  /**
   * Find a node in the data-tree based on the tag(ID) or the first node without parent
   * That is: if we can't find a match, send back to Root (first question).
   * @node
   */
  const findNode = (node) => {
    const nextNodeToLoad = node
      ? tree.find((x) => x.tag === node)
      : tree.find((x) => !x.parent);
    return nextNodeToLoad;
  };

  const countTreeQuestions = (rootNode, currentQuestion) => {
    let maxDepth = currentQuestion;
    traverse(rootNode, currentQuestion);

    function traverse(node, depth) {
      if (!node.buttons || node.buttons.length === 0) {
        if (depth > maxDepth) {
          maxDepth = depth;
        }
        return;
      }

      node.buttons.forEach(button => {
        traverse(findNode(button.nextTag), depth + 1);
      });
    }

    return maxDepth;
  };

  const { trackPageView } = useMatomo();

  const [currentNode, setCurrentNode] = useState(findNode());
  const [history, setHistory] = useState([]);
  const [store, setStore] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [maxQuestion, setMaxQuestion] = useState(countTreeQuestions(currentNode, 0));

  useEffect(() => {
    const currentPage = currentNode.page || currentNode.tag;
    if (window.location.hostname === 'localhost') return;
    try {
      trackPageView({ documentTitle: currentPage });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Could not send pageview. Details:', error);
    }
  }, [currentNode.page, currentNode.tag, trackPageView]);

  /** Render the next node in the data-tree based on the answer of the current */
  const renderNext = (answer) => {
    const { label, nextTag } = answer;
    // Hack: next line is for development purposes
    // eslint-disable-next-line no-console
    if (module.hot) console.info(`The answer was [${label}] for tag [${currentNode.tag}]. Next: [${nextTag}]`);

    if (currentNode && currentNode.question) {
      store[currentNode.question] = label;
      setStore(store);
    }

    const nextNode = findNode(nextTag);
    history.push(currentNode);
    const nextQuestion = currentQuestion + 1;
    setCurrentNode(nextNode);
    setHistory(history);
    setCurrentQuestion(nextQuestion);
    setMaxQuestion(countTreeQuestions(nextNode, nextQuestion));
  };

  /** Render the previous node in the data-tree based on history */
  const renderPrevious = () => {
    // no previous page...
    if (history.length <= 0) {
      return;
    }

    const previousNode = history[history.length - 1];

    if (previousNode && previousNode.question) {
      store[previousNode.question] = null;
      setStore(store);
    }

    history.pop();
    const previousQuestion = currentQuestion - 1;

    setCurrentNode(previousNode);
    setHistory(history);
    setCurrentQuestion(previousQuestion);
    setMaxQuestion(countTreeQuestions(previousNode, previousQuestion));
  };

  const { component } = currentNode;
  const Component = components[component];
  const renderAwareNote = currentNode.head && currentNode.type === 'Conclusion';
  return (
    <>
      <Header
        showBackButton={history.length > 0}
        sessionStore={store}
        awareNote={renderAwareNote}
        onClick={(event) => {
          event.preventDefault();
          renderPrevious();
        }}
        currentQuestion={currentQuestion}
        maxQuestion={maxQuestion}
      />
      <Component onClick={renderNext} {...currentNode} />
    </>
  );
}

Wizard.propTypes = {
  // version: PropTypes.string.isRequired, // still not sure if we need it
  tree: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default Wizard;
